import { Link } from 'react-router-dom'
import { Container, Section, Heading, Text, StepCard, Button } from '.'

// Code snippet components
const IntegrateSnippet = () => (
  <div className="rounded-xl bg-slate-50 border border-slate-200 p-6 font-mono text-sm">
    <div className="space-y-2">
      <div className="text-slate-500"># Add the SDK to your app</div>
      <div className="text-green-400">npm install @roselabs/falcon-js</div>
      <div className="mt-3 text-slate-500"># Initialize with your API key</div>
      <div className="text-blue-400">
        import {'{'} Falcon {'}'} from '@roselabs/falcon-js'
      </div>
      <div className="text-blue-400">
        Falcon.init({'{'} apiKey: 'YOUR_API_KEY' {'}'})
      </div>
      <div className="mt-3 text-slate-400 text-xs">Errors are captured automatically</div>
    </div>
  </div>
)

const MonitorSnippet = () => (
  <div className="rounded-xl bg-slate-50 border border-slate-200 p-6 font-mono text-sm">
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
        <span className="text-green-400">All systems operational</span>
      </div>
      <div className="pl-4 space-y-1 mt-2">
        <div className="text-slate-600">
          <span className="text-green-400 font-bold">99.9%</span> uptime (24h)
        </div>
        <div className="text-slate-600">
          <span className="text-blue-400 font-bold">3</span> errors tracked
        </div>
        <div className="text-slate-600">
          <span className="text-purple-400">142ms</span> avg response
        </div>
      </div>
      <div className="mt-3 pt-3 border-t border-slate-300">
        <div className="text-blue-400">→ View app dashboard</div>
      </div>
    </div>
  </div>
)

const AlertSnippet = () => (
  <div className="rounded-xl bg-slate-50 border border-slate-200 p-6">
    <div className="space-y-4">
      {/* Email preview */}
      <div className="bg-slate-200/50 rounded-lg p-4 border border-slate-300">
        <div className="flex items-start gap-3">
          <svg
            className="w-6 h-6 text-primary-400 flex-shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
          <div className="flex-1 space-y-2">
            <div className="text-sm">
              <div className="text-slate-600">
                To: <span className="text-white">team@yourcompany.com</span>
              </div>
              <div className="text-slate-600">
                Subject: <span className="text-red-400 font-semibold">App down: my-api</span>
              </div>
            </div>
            <div className="text-xs text-slate-500 pt-2 border-t border-slate-300">
              Health check failed at 2:45 PM UTC
            </div>
          </div>
        </div>
      </div>

      {/* Status indicator */}
      <div className="flex items-center gap-2 text-sm">
        <svg
          className="w-4 h-4 text-green-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <span className="text-slate-600">Monitoring: Every 5 minutes</span>
      </div>
    </div>
  </div>
)

export function HowItWorksSection() {
  const steps = [
    {
      number: 1,
      title: 'Integrate Your App',
      description:
        'Add our lightweight SDK to automatically capture errors. Available for JavaScript, Python, React, and Next.js. Configure uptime monitoring by setting a health check URL.',
      visual: <IntegrateSnippet />,
    },
    {
      number: 2,
      title: 'Monitor in Real-Time',
      description:
        'See errors as they happen, track uptime and response times, and monitor custom metrics. Your dashboard shows everything at a glance.',
      visual: <MonitorSnippet />,
    },
    {
      number: 3,
      title: 'Get Alerted Instantly',
      description:
        'Set up alert profiles to receive notifications via email, Slack, or webhooks. Configure cooldowns to avoid alert fatigue and route alerts to the right team.',
      visual: <AlertSnippet />,
    },
  ]

  return (
    <Section className="relative overflow-hidden bg-slate-300">
      {/* Subtle decorative elements */}
      <div className="absolute top-1/4 left-0 w-96 h-96 bg-primary-500/5 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-accent-500/5 rounded-full blur-3xl" />

      <Container className="relative z-10">
        <div className="space-y-16">
          {/* Section header */}
          <div className="text-center space-y-4 max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-100 border border-primary-300">
              <span className="text-sm text-primary-800 font-medium">How It Works</span>
            </div>

            <Heading as="h2" className="text-4xl md:text-5xl font-bold">
              <span className="text-slate-900">Monitoring in</span>
              <br />
              <span className="bg-gradient-to-r from-primary-600 to-accent-500 bg-clip-text text-transparent">
                three simple steps
              </span>
            </Heading>

            <Text size="lg" className="text-slate-600">
              From integration to alerts in under 5 minutes. No complex setup, no learning curve.
            </Text>
          </div>

          {/* Steps with connectors */}
          <div className="relative max-w-4xl mx-auto">
            <div className="space-y-16">
              {steps.map((step, index) => (
                <div key={index} className="relative">
                  <StepCard
                    number={step.number}
                    title={step.title}
                    description={step.description}
                    visual={step.visual}
                  />

                  {/* Connector line */}
                  {index < steps.length - 1 && (
                    <div className="absolute left-4 top-full h-16 w-0.5 bg-gradient-to-b from-primary-500/50 to-purple-500/50 -translate-y-2 hidden md:block" />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Bottom CTA */}
          <div className="text-center space-y-6">
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/signup">
                <Button variant="primary" size="lg">
                  Start Free Trial
                </Button>
              </Link>
              <Link to="/docs">
                <Button variant="outline" size="lg">
                  View Documentation
                </Button>
              </Link>
            </div>
            <Text className="text-slate-500 text-sm">
              No credit card required • Full access free trial
            </Text>
          </div>
        </div>
      </Container>
    </Section>
  )
}
